import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { SectionLabel, HighlightText } from "@/components/Bits";
import FinalCTA from "@/components/FinalCTA";
import { CALENDLY_URL } from "@/lib/api";

const STAGES = [
  {
    n: "01", tag: "Discovery",
    title: "You talk.",
    italic: "We listen.",
    desc: "A short conversation with our team is all it takes to get started. You describe the support you need, the tools you use, how you prefer to work and what success looks like for you. We ask targeted questions to fully understand your workflow and standards.",
    glance: { label: "Average discovery call length", value: "28", unit: "minutes", note: "Done over Zoom, Google Meet, or a simple phone call." },
    bullets: ["Communication style and quality standards", "The specific support you need", "Tools, workflows, and systems you use", "Your timezone and operating rhythm"],
    note: "Unlike platforms where you do all the heavy lifting upfront, we make it simple. You talk, we capture the details and handle the rest.",
  },
  {
    n: "02", tag: "Matching", dark: true,
    title: "We assign your person.",
    italic: "Already vetted, always ready.",
    desc: "Our operations team assigns a pre-aligned professional based on your workflow, tools, and expectations. You don't review resumes. You don't run interviews. We don't send options, we send someone ready to execute.",
    glance: { label: "Matches delivered within hours, not weeks.", value: "2h", unit: "first match", note: "You only connect with a highly aligned candidate." },
    bullets: ["Skills and tools assessment", "US business standards training", "Background and reference checks", "English communication and business etiquette"],
    note: "Traditional freelance platforms often mean sifting through dozens of profiles and hoping for the best. With Aidify, that ends.",
  },
  {
    n: "03", tag: "US Partner Intro",
    title: "Meet the person accountable.",
    italic: "Local ownership. Global delivery.",
    desc: "Before work begins, you're introduced to your named US-based partner. This person serves as your primary point of contact and takes full ownership for the success of the engagement; handling performance, any adjustments, and ensuring everything runs smoothly.",
    glance: { label: "Every placement has a named US partner.", value: "1:1", unit: "named partner", note: "Reachable by phone, email, and video call." },
    bullets: ["Aligning on performance expectations", "Available for real conversations and escalations", "Ensuring smooth integration with your team", "Ongoing quality oversight"],
    note: "This is what sets Aidify apart from most virtual assistant providers and freelance platforms. Not an anonymous support channel or ticket system; a real US-based professional you can reach directly by phone or video when needed.",
  },
  {
    n: "04", tag: "Setup & Onboarding", dark: true,
    title: "We handle the onboarding.",
    italic: "So day one feels like day ten.",
    desc: "Your virtual team member receives a thorough briefing on your specific workflows, tools, brand voice, and expectations before they ever start. By the time they show up in your Slack or inbox, they already know what they are doing.",
    glance: { label: "Full briefing completed before day one.", value: "0", unit: "ramp days", note: "Tooled up, aligned, and ready to deliver." },
    bullets: ["Tool access & workflow walkthrough", "Role-specific briefing and priorities", "Brand voice and communication standards", "Introduction to your team and tools"],
    note: "We absorb the back-and-forth so your virtual team member hits the ground running and your existing team experiences minimal disruption.",
  },
  {
    n: "05", tag: "Day One",
    title: "They show up ready.",
    italic: "You just say go.",
    desc: "Your dedicated virtual team member plugs directly into your existing workflows from the first day; using your tools, matching your tone and following your priorities. No extended ramp-up period.",
    glance: { label: "Contributing from hour one.", value: "Day 1", unit: "productive", note: "Your team experiences almost zero added onboarding load." },
    bullets: ["Output from hour one", "Plugged into your existing workflow", "Aligned on tone and tools", "Onboarding you barely notice"],
    note: "With typical freelance or new-hire setups, productivity often takes days or weeks to materialize. With Aidify, the question of \"when will they become useful?\" rarely comes up.",
  },
  {
    n: "06", tag: "Ongoing Oversight", dark: true,
    title: "We stay involved.",
    italic: "So standards never slip.",
    desc: "Aidify doesn't hand off and disappear. Your US partner continues monitoring progress, catching potential issues early and ensuring the support consistently meets (or exceeds) your standards as your needs evolve.",
    glance: { label: "24/7 US oversight on every engagement.", value: "24/7", unit: "oversight", note: "Potential issues addressed before they impact you." },
    bullets: ["Regular performance check-ins", "Quick adjustments when needed", "Proactive partner communication", "Continuous refinement to match changing priorities"],
    note: "Many virtual assistant or freelance arrangements look good initially but can drift without active management. We prevent that through proactive US-level oversight.",
  },
  {
    n: "07", tag: "Scale",
    title: "When you're ready to grow,",
    italic: "we're already prepared.",
    desc: "Adding more support or expanding to new areas doesn't require starting over. Your US partner already understands your business, standards and workflows. Scaling is usually just one additional conversation.",
    glance: { label: "Most clients add a second professional within 60 days.", value: "60d", unit: "to scale", note: "Your profile and standards are already on file." },
    bullets: ["Add support across functions without new processes", "Your established standards transfer automatically", "One ongoing relationship for all your virtual team members", "Unlimited growth potential with the same level of accountability"],
    note: "Many businesses stall not from lack of opportunity, but from the friction of finding and onboarding reliable help. With Aidify, expanding your support team is straightforward and consistent.",
  },
];

export default function HowItWorks() {
  return (
    <>
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16">
        <SectionLabel>How it works</SectionLabel>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-6 items-end">
          <div className="lg:col-span-8" data-reveal>
            <h1 className="font-display font-extrabold tracking-tight text-forest-600 text-5xl md:text-7xl lg:text-[80px] leading-[0.95]">
              From Conversation
              <br />
              <span className="font-serif-italic font-normal">to fully </span>
              <HighlightText><span className="font-serif-italic font-normal">operational.</span></HighlightText>
            </h1>
            <p className="mt-7 max-w-xl text-forest-400 text-[16px] leading-relaxed">
              No job posts. No interviews. No back and forth with freelancers. Just tell us what you need and we take it from there. Here's exactly how your dedicated offshore virtual resource becomes a seamless part of your operations.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="btn-primary">Book Your Discovery Call <ArrowRight size={16} /></a>
              <a href="#stagess" className="btn-ghost">See the 7 stages <ArrowRight size={14} /></a>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="aidify-card-dark p-6" data-reveal>
              <div className="text-xs uppercase tracking-[0.18em] text-cream-100/60 mb-3">Aidify Promise</div>
              <p className="font-serif-italic text-cream-100 text-xl leading-snug">
                "Most businesses spend 3 to 6 weeks finding the right person. Aidify clients are up and running in hours."
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="stagess"> 
        {/* This is where the Seven Stages are rendered */}
      </section>

      <section className="mt-10"> 
        {/* This adds roughly 40px of empty space above this section */}
      </section>

      {/* STAGES */}
      <section id="stages" className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24 space-y-5">
        {STAGES.map((s, i) => (
          <div key={i} className={`p-7 lg:p-10 ${s.dark ? "aidify-card-dark" : "aidify-card"}`} data-reveal>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-8">
                <div className={`flex items-baseline gap-4 ${s.dark ? "text-cream-100/60" : "text-forest-400/60"}`}>
                  <span className="font-display font-extrabold text-5xl">{s.n}</span>
                  <span className="text-xs uppercase tracking-[0.2em]">{s.tag}</span>
                </div>
                <h3 className={`mt-3 font-display font-extrabold text-3xl md:text-4xl leading-tight ${s.dark ? "text-cream-100" : "text-forest-600"}`}>
                  {s.title}
                  <br />
                  <span className="font-serif-italic font-normal opacity-90">{s.italic}</span>
                </h3>
                <p className={`mt-5 text-sm leading-relaxed ${s.dark ? "text-cream-100/75" : "text-forest-400"}`}>{s.desc}</p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {s.bullets.map((b, j) => (
                    <div key={j} className={`flex items-start gap-2 text-sm ${s.dark ? "text-cream-100/85" : "text-forest-500"}`}>
                      <CheckCircle2 size={14} className={`mt-0.5 ${s.dark ? "text-sage-200" : "text-sage-500"}`} />
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
                <p className={`mt-5 text-xs italic ${s.dark ? "text-cream-100/55" : "text-forest-400/70"}`}>{s.note}</p>
              </div>
              <div className="lg:col-span-4">
                <div className={`p-5 rounded-2xl h-full ${s.dark ? "bg-forest-500/40" : "bg-cream-200/60"}`}>
                  <div className={`text-xs uppercase tracking-[0.18em] mb-3 ${s.dark ? "text-cream-100/60" : "text-forest-400/70"}`}>— At a glance</div>
                  <div className={`font-display font-extrabold text-3xl ${s.dark ? "text-cream-100" : "text-forest-600"}`}>{s.glance.value}</div>
                  <div className={`text-[10px] uppercase tracking-wider ${s.dark ? "text-cream-100/60" : "text-forest-500/70"}`}>{s.glance.unit}</div>
                  <p className={`mt-4 text-sm font-medium ${s.dark ? "text-cream-100" : "text-forest-600"}`}>{s.glance.label}</p>
                  <p className={`mt-2 text-xs ${s.dark ? "text-cream-100/65" : "text-forest-400"}`}>{s.glance.note}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      <FinalCTA />
    </>
  );
}
